import { cn } from '@/lib/utils';

/**
 * `<LivePollingIndicator>` — UI-SPEC § 3.7 "● Updating live" affordance.
 *
 * Renders a pulsing tide-400 dot + "Updating live" text while polling. When
 * `isPolling` flips to false, the indicator fades out via 200ms opacity
 * transition (motion-reduce → instant) and announces the final status via
 * `aria-live=polite`.
 *
 * Reduced-motion: the indicator dot stops pulsing; the fade is instant.
 *
 * Accessibility:
 * - `role="status"` + `aria-live="polite"` so SR users hear the state change
 *   when polling stops.
 * - `aria-atomic="false"` so only the changed text is announced (not the
 *   entire region) on each update.
 */
export interface LivePollingIndicatorProps {
  isPolling: boolean;
  /** Optional last-update time for tooltip on hover. */
  lastUpdated?: Date | null;
  /** Final status to announce when polling stops (e.g. 'completed' or 'failed'). */
  finalStatus?: 'completed' | 'failed';
}

export function LivePollingIndicator({
  isPolling,
  lastUpdated,
  finalStatus,
}: LivePollingIndicatorProps) {
  const stoppedMsg = finalStatus
    ? `Updates stopped — job is ${finalStatus}.`
    : 'Updates stopped.';

  const tooltipText = lastUpdated
    ? `Last updated: ${Math.max(0, Math.round((Date.now() - lastUpdated.getTime()) / 1000))}s ago`
    : undefined;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="false"
      title={tooltipText}
      className={cn(
        'inline-flex items-center gap-2 h-6',
        'transition-opacity duration-200 motion-reduce:transition-none',
        isPolling ? 'opacity-100' : 'opacity-0 pointer-events-none',
      )}
    >
      <span
        aria-hidden="true"
        className="w-2 h-2 rounded-full bg-tide-400 animate-pulse motion-reduce:animate-none"
      />
      <span className="text-sm font-semibold text-tide-400">
        {isPolling ? 'Updating live' : stoppedMsg}
      </span>
    </div>
  );
}
